import { CircularProgress } from '@mui/material';
import clsx from 'clsx';
import type { ButtonHTMLAttributes, FC, PropsWithChildren, Ref } from 'react';
import { forwardRef } from 'react';
import { Link, LinkProps } from 'react-router';

import type { XOR } from '@/utils/types';

type BaseProps = {
  size?: 'small' | 'medium' | 'large';
  type?: 'button' | 'submit';
  startIcon?: FC<{ className?: string }>;
  startIconClassName?: string;
  endIcon?: FC<{ className?: string }>;
  endIconClassName?: string;
  srText?: string;
  loading?: boolean;
  loadingClassName?: string;
  'data-testid'?: string;
  omitPaddingClassnames?: boolean;
};

type ButtonElementProps = PropsWithChildren<ButtonHTMLAttributes<HTMLButtonElement>> & BaseProps;

type LinkElementProps = LinkProps & BaseProps;

export type ButtonBaseProps = XOR<ButtonElementProps, LinkElementProps>;

export const testId = 'of-button';

const ButtonBase = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonBaseProps>(
  (
    {
      className,
      children,
      size = 'medium',
      type = 'button',
      startIcon: StartIcon,
      startIconClassName,
      endIcon: EndIcon,
      endIconClassName,
      srText,
      to,
      loading,
      loadingClassName,
      omitPaddingClassnames,
      ...props
    },
    ref
  ) => {
    const iconClassName = clsx({
      'w-16 h-16': size === 'small' || size === 'medium',
      'w-20 h-20': size === 'large',
    });

    const paddingClassNames = omitPaddingClassnames
      ? ''
      : clsx({
          'py-6 px-8': size === 'small' && children,
          'px-12 py-8': size === 'medium' && children,
          'p-12': size === 'large' && children,
          'p-6': size === 'small' && !children,
          'p-8': size === 'medium' && !children,
          'p-10': size === 'large' && !children,
        });

    const allClassNames = clsx(
      className,
      'rounded-sm whitespace-nowrap',
      'disabled:cursor-not-allowed',
      paddingClassNames,
      {
        'relative !text-transparent': !!loading,
        'text-14 font-bold leading-[20px]': size === 'small',
        'text-16 font-bold leading-normal': size === 'medium',
        'text-18 font-bold leading-normal': size === 'large',
        'flex items-center gap-8': !!StartIcon || !!EndIcon,
      }
    );

    const isButton = !to;

    const Content = () => (
      <>
        {StartIcon ? (
          <StartIcon className={clsx(iconClassName, startIconClassName)} aria-hidden="true" />
        ) : null}
        {children}
        {srText && !children ? <span className="sr-only">{srText}</span> : null}
        {EndIcon ? (
          <EndIcon
            className={clsx(iconClassName, endIconClassName, 'ml-auto')}
            aria-hidden="true"
          />
        ) : null}
        {loading ? (
          <span className={clsx(loadingClassName, 'inset-0 absolute grid place-content-center')}>
            <CircularProgress color="inherit" size={12} thickness={5} />
          </span>
        ) : null}
      </>
    );

    return isButton ? (
      <button
        ref={ref as Ref<HTMLButtonElement>}
        className={allClassNames}
        type={type}
        data-testid={testId}
        {...(props as ButtonElementProps)}
      >
        <Content />
      </button>
    ) : (
      <Link
        ref={ref as Ref<HTMLAnchorElement>}
        className={clsx(allClassNames, 'inline-block')}
        to={to}
        data-testid={testId}
        {...(props as Omit<LinkElementProps, 'to'>)}
      >
        <Content />
      </Link>
    );
  }
);

ButtonBase.displayName = 'Button';

const PrimaryButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonBaseProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ButtonBase
        ref={ref}
        className={clsx(
          className,
          // brand-primary turns bright teal in dark mode, so the label flips to dark ink there
          // and the hover moves to the brighter hover token instead of near-black teal-950.
          'bg-brand-primary text-surface-card',
          'shadow-5 transition-shadow hover:shadow-1',
          'hover:bg-brand-secondary dark:hover:bg-brand-primary-hover',
          'disabled:bg-disabled-primary/50 disabled:text-disabled-secondary/50 disabled:shadow-none'
        )}
        loadingClassName="text-surface-card"
        {...props}
      >
        {children}
      </ButtonBase>
    );
  }
);

PrimaryButton.displayName = 'PrimaryButton';

const SecondaryButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonBaseProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ButtonBase
        ref={ref}
        className={clsx(
          className,
          'bg-surface-secondary text-on-surface',
          'shadow-5 transition-shadow hover:shadow-1',
          '[&:not(:disabled):hover]:bg-surface-muted',
          'disabled:text-on-surface-subtle disabled:shadow-none'
        )}
        loadingClassName="text-on-surface"
        {...props}
      >
        {children}
      </ButtonBase>
    );
  }
);

SecondaryButton.displayName = 'SecondaryButton';

const DestructiveButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonBaseProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ButtonBase
        ref={ref}
        className={clsx(
          className,
          'bg-destructive-primary text-white',
          'shadow-5 transition-shadow hover:shadow-1',
          '[&:not(:disabled):hover]:bg-destructive-secondary',
          'disabled:bg-red-200 disabled:text-destructive-primary disabled:shadow-none'
        )}
        loadingClassName="text-white"
        {...props}
      >
        {children}
      </ButtonBase>
    );
  }
);

DestructiveButton.displayName = 'DestructiveButton';

const TertiaryButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonBaseProps & { invert?: boolean }
>(({ className, children, size = 'medium', invert, ...props }, ref) => {
  const paddingClassNames = clsx({
    'px-8': size === 'small' && children,
    'px-12': (size === 'medium' || size === 'large') && children,
    'p-6': size === 'small' && !children,
    'p-8': size === 'medium' && !children,
    'p-10': size === 'large' && !children,
  });
  const colorClassNames = invert
    ? clsx('text-white', '[&:not(:disabled):hover]:text-gray-200', 'disabled:text-gray-300')
    : clsx(
        'text-brand-tertiary',
        '[&:not(:disabled):hover]:text-teal-700',
        'disabled:text-disabled-primary'
      );
  return (
    <ButtonBase
      ref={ref}
      className={clsx(className, paddingClassNames, colorClassNames)}
      loadingClassName="text-brand-tertiary"
      omitPaddingClassnames
      size={size}
      {...props}
    >
      {children}
    </ButtonBase>
  );
});

TertiaryButton.displayName = 'TertiaryButton';

export type ConditionalButtonProps =
  | {
      variant?: 'tertiary';
      invert?: boolean;
    }
  | {
      variant?: 'primary' | 'secondary' | 'destructive';
      invert?: never;
    };

export type ButtonProps = ButtonBaseProps & ConditionalButtonProps;

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  ({ variant = 'primary', ...props }, ref) => {
    switch (variant) {
      case 'primary':
        return <PrimaryButton ref={ref} {...props} />;
      case 'secondary':
        return <SecondaryButton ref={ref} {...props} />;
      case 'tertiary':
        return <TertiaryButton ref={ref} {...props} />;
      case 'destructive':
        return <DestructiveButton ref={ref} {...props} />;
    }
  }
);

Button.displayName = 'Button';

export default Button;
